import { DEFAULT_BRAND } from "@shared/brand.types";
import type { EspaceOverview, EspaceSchool } from "@shared/espace.types";
import { Briefcase, Building2, LayoutDashboard, LogOut, Plus, Users } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { BrandRoot } from "@/components/BrandRoot";
import { Avatar } from "@/components/ds/Avatar";
import { Button } from "@/components/ds/Button";
import { type EntreeNav, EspaceLayout } from "@/components/EspaceLayout";
import { PageMessage } from "@/components/PageMessage";
import { fetchJson } from "@/lib/http";
import { CompanyPage } from "./CompanyPage";
import { DashboardPage } from "./DashboardPage";
import { ProfilesPage, SelectionDetailPage } from "./ProfilesPage";
import { RequestDetailPage, RequestsPage } from "./RequestsPage";
import { useEspace } from "./useEspace";

/**
 * L'espace, une fois le code saisi.
 *
 * Il prend la marque de l'ÉCOLE avec qui l'entreprise travaille — la première de
 * la liste, ou la seule. C'est la différence avec la page de dépôt, qui connaît son
 * école par l'URL : ici, elle se déduit de ce que l'entreprise a déjà déposé ou
 * reçu. Sans école (un contact sans historique), la marque Mentor Goal prend le
 * relais plutôt que de laisser une coque grise.
 */
export function EspacePage() {
	const { state, answer, saveCompany, redeposer } = useEspace();
	const navigate = useNavigate();

	if (state.status === "loading") {
		return (
			<div className="flex min-h-screen items-center justify-center bg-mg-page">
				<p className="t-body text-mg-secondary">Chargement…</p>
			</div>
		);
	}

	// Session absente ou expirée : le code, pas un message d'erreur.
	if (state.status === "anonymous") return <Navigate to="/mes-demandes" replace />;

	if (state.status === "failed") {
		// La cause vient de `messageDeSecours` : « le serveur met trop de temps »,
		// « la connexion a échoué », « le service est momentanément indisponible ».
		// Le titre seul faisait cliquer « Réessayer » sur un problème de connexion,
		// qui ne se règle pas en rechargeant.
		return (
			<PageMessage
				title="L'espace n'a pas pu s'ouvrir"
				body={state.message}
				retry={{ label: "Réessayer", onClick: () => window.location.reload() }}
			/>
		);
	}

	const overview = state.overview;
	const school: EspaceSchool | null = overview.schools[0] ?? null;
	const brand = {
		...DEFAULT_BRAND,
		slug: school?.slug ?? DEFAULT_BRAND.slug,
		schoolName: school?.name ?? DEFAULT_BRAND.schoolName,
		logoUrl: school?.logoUrl ?? null,
		accent: school?.accent ?? DEFAULT_BRAND.accent,
		// Ces deux-là ne remontaient pas : la requête de l'espace ne demandait que
		// `accent`. Une école qui avait réglé son contraste ou son aplat ne le voyait
		// nulle part — un champ transporté à moitié ne se voit pas, il s'ignore.
		accentContrast: school?.accentContrast ?? DEFAULT_BRAND.accentContrast,
		navBase: school?.navBase ?? null,
	};

	const deposit = () => navigate(`/e/${brand.slug}`);

	return (
		<BrandRoot brand={brand}>
			<CoqueEntreprise overview={overview} school={school} onDeposit={deposit}>
				<Routes>
					<Route index element={<DashboardPage overview={overview} onDeposit={deposit} />} />
					<Route path="offres" element={<RequestsPage overview={overview} onDeposit={deposit} />} />
					<Route
						path="offres/:id"
						element={<RequestDetailPage overview={overview} onRedeposer={redeposer} />}
					/>
					<Route path="profils" element={<ProfilesPage overview={overview} />} />
					<Route
						path="profils/:id"
						element={<SelectionDetailPage overview={overview} onAnswer={answer} />}
					/>
					<Route
						path="entreprise"
						element={<CompanyPage overview={overview} onSave={saveCompany} />}
					/>
					<Route path="*" element={<Navigate to="/espace" replace />} />
				</Routes>
			</CoqueEntreprise>
		</BrandRoot>
	);
}

function entreesEntreprise(overview: EspaceOverview): EntreeNav[] {
	// On ne compte pas les profils, on compte CEUX QUI ATTENDENT une réponse :
	// un total qui ne bouge jamais cesse d'être lu au bout de deux visites.
	const enAttente = overview.selections.reduce(
		(total, selection) =>
			total + selection.profiles.filter((profile) => profile.decision === null).length,
		0,
	);
	return [
		{
			to: "/espace",
			end: true,
			label: "Tableau de bord",
			shortLabel: "Accueil",
			icon: LayoutDashboard,
		},
		{
			to: "/espace/offres",
			label: "Mes offres",
			shortLabel: "Offres",
			icon: Briefcase,
			badge: overview.requests.length,
		},
		{
			to: "/espace/profils",
			label: "Profils reçus",
			shortLabel: "Profils",
			icon: Users,
			badge: enAttente,
		},
		{
			to: "/espace/entreprise",
			label: "Mon entreprise",
			shortLabel: "Entreprise",
			icon: Building2,
		},
	];
}

/** La coque, remplie pour l'entreprise : son nom, le dépôt, la sortie. */
function CoqueEntreprise({
	overview,
	school,
	onDeposit,
	children,
}: {
	overview: EspaceOverview;
	school: EspaceSchool | null;
	onDeposit: () => void;
	children: ReactNode;
}) {
	const navigate = useNavigate();
	const companyName = overview.company?.name ?? "Votre entreprise";
	const [echecSortie, setEchecSortie] = useState(false);

	/*
	 * ⚠️ Le cookie est httpOnly : SEUL le serveur peut fermer la session. Quand
	 * l'appel échoue, il ne s'est rien passé, et partir vers `/mes-demandes` ne
	 * ferme rien du tout : cette page monte la porte à code, dont la sonde
	 * `/api/espace` repart avec le cookie toujours vivant, répond 200, et renvoie
	 * l'entreprise dans son espace. Elle croit être sortie, sa session tient encore
	 * trente jours, et sur un poste partagé c'est le contraire de ce qu'elle a
	 * demandé. On reste donc sur place, et on le dit.
	 */
	async function logout() {
		setEchecSortie(false);
		try {
			await fetchJson("/api/requests/logout", { method: "POST" });
		} catch {
			setEchecSortie(true);
			return;
		}
		navigate("/mes-demandes");
	}

	return (
		<EspaceLayout
			navLabel="Espace entreprise"
			logoUrl={school?.logoUrl ?? null}
			name={school?.name ?? "Mentor Goal"}
			caption="Espace partenaire"
			entries={entreesEntreprise(overview)}
			user={
				<>
					<Avatar name={companyName} size="sm" />
					<span className="t-label truncate text-mg-heading">{companyName}</span>
				</>
			}
			actions={
				<>
					<Button tone="primary" size="sm" icon={<Plus className="size-4" />} onClick={onDeposit}>
						<span className="hidden sm:inline">Déposer une offre</span>
						<span className="sm:hidden">Déposer</span>
					</Button>
					<Button
						tone="tertiary"
						size="sm"
						onClick={logout}
						aria-label="Fermer la session"
						icon={<LogOut className="size-4" />}
					/>
				</>
			}
			alert={
				echecSortie ? (
					<p role="alert" className="t-body bg-mg-error-subtle px-5 py-3 text-mg-error-fg">
						La session n'a pas pu être fermée. Réessayez : tant que le serveur n'a pas répondu, elle
						reste ouverte.
					</p>
				) : null
			}
		>
			{children}
		</EspaceLayout>
	);
}
